import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

// Create theme context
const SuccessionThemeContext = createContext();

// Custom hook to use theme context
export const useSuccessionTheme = () => {
  const context = useContext(SuccessionThemeContext);
  if (!context) {
    throw new Error('useSuccessionTheme must be used within a SuccessionThemeProvider');
  }
  return context;
};

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Helper function to determine if a color is light or dark
const isLightColor = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return true;
  
  // Calculate luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5;
};

const SuccessionThemeProvider = ({ children, company = {}, user = {} }) => {
  const [theme, setTheme] = useState('light');
  const [systemPreference, setSystemPreference] = useState('light');
  const [isInitialized, setIsInitialized] = useState(false);

  // Process company colors once (memoized to prevent unnecessary recalculations)
  const companyColors = useMemo(() => {
    return {
      primary_color: company.primary_color || '#5489B6',
      secondary_color: company.secondary_color || '#002949',
      neutral_color: company.neutral_color || '#666666',
      accent_color: company.accent_color || '#5B5E5A',
      background_color: company.background_color || '#ffffff',
      // Avatar settings for themed backgrounds
      avatar_background_color: company.avatar_background_color,
      avatar_directory: company.avatar_directory,
    };
  }, [
    company.primary_color, 
    company.secondary_color, 
    company.neutral_color, 
    company.accent_color, 
    company.background_color,
    company.avatar_background_color,
    company.avatar_directory
  ]);

  // Apply CSS variables immediately on mount (before any renders)
  useEffect(() => {
    const root = document.documentElement;
    
    // Convert hex colors to RGB for opacity variants
    const primaryRgb = hexToRgb(companyColors.primary_color);
    const secondaryRgb = hexToRgb(companyColors.secondary_color);
    const accentRgb = hexToRgb(companyColors.accent_color);
    const neutralRgb = hexToRgb(companyColors.neutral_color);

    // Enhance existing CSS variables (layout JavaScript sets basics, we add details)
    if (primaryRgb) {
      // Only set if not already set by layout
      if (!root.style.getPropertyValue('--succession-primary')) {
        root.style.setProperty('--succession-primary', companyColors.primary_color);
      }
      // Add additional variables not set by layout
      root.style.setProperty('--company-primary', companyColors.primary_color);
      root.style.setProperty('--company-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
      root.style.setProperty('--succession-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
      root.style.setProperty('--succession-primary-10', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`);
      root.style.setProperty('--succession-primary-20', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.2)`);
    }
    
    if (secondaryRgb) {
      if (!root.style.getPropertyValue('--succession-secondary')) {
        root.style.setProperty('--succession-secondary', companyColors.secondary_color);
      }
      root.style.setProperty('--company-secondary', companyColors.secondary_color);
      root.style.setProperty('--succession-secondary-10', `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.1)`);
    }
    
    if (accentRgb) {
      if (!root.style.getPropertyValue('--succession-accent')) {
        root.style.setProperty('--succession-accent', companyColors.accent_color);
      }
      root.style.setProperty('--company-accent', companyColors.accent_color);
      root.style.setProperty('--succession-accent-10', `rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.1)`);
    }
    
    if (neutralRgb) {
      if (!root.style.getPropertyValue('--succession-neutral')) {
        root.style.setProperty('--succession-neutral', companyColors.neutral_color);
      }
      root.style.setProperty('--company-neutral', companyColors.neutral_color);
    }

    // Set background variables based on theme
    const savedTheme = localStorage.getItem('succession-theme') || 'light';
    applyTheme(savedTheme);
    
  }, []); // Run once on mount

  // Apply theme-specific CSS variables
  const applyTheme = useCallback((currentTheme) => {
    const root = document.documentElement;
    const primaryRgb = hexToRgb(companyColors.primary_color);
    
    // Remove existing theme classes
    root.classList.remove('succession-light', 'succession-dark');
    
    // Add theme class
    root.classList.add(`succession-${currentTheme}`);
    
    // Preserve marketplace colors and only update background/text colors
    if (currentTheme === 'light') {
      // Demo-inspired light theme - clean, minimalist design
      root.style.setProperty('--succession-bg-primary', '#ffffff');
      root.style.setProperty('--succession-bg-secondary', '#F9FAFB');
      root.style.setProperty('--succession-bg-tertiary', '#F3F4F6');
      root.style.setProperty('--succession-text-primary', '#111827');
      root.style.setProperty('--succession-text-secondary', '#6B7280');
      root.style.setProperty('--succession-text-tertiary', '#9CA3AF');
      root.style.setProperty('--succession-text-muted', '#9CA3AF');
      root.style.setProperty('--succession-border', '#E5E7EB');
      root.style.setProperty('--succession-border-hover', '#D1D5DB');
      root.style.setProperty('--succession-hover-bg', '#F9FAFB');
      root.style.setProperty('--succession-active-bg', '#F3F4F6');
      root.style.setProperty('--succession-focus-ring', 'rgba(74, 144, 226, 0.3)');
      
      // Demo-specific message colors
      root.style.setProperty('--succession-message-sent-bg', '#4A90E2');
      root.style.setProperty('--succession-message-sent-text', '#ffffff');
      root.style.setProperty('--succession-message-received-bg', '#F3F4F6');
      root.style.setProperty('--succession-message-received-text', '#111827');
      root.style.setProperty('--succession-primary-demo', '#4A90E2');
      root.style.setProperty('--succession-primary-hover-demo', '#3A7BC8');
    } else {
      // Dark theme background and text colors (high contrast for visibility)
      root.style.setProperty('--succession-bg-primary', '#1F2937');
      root.style.setProperty('--succession-bg-secondary', '#111827');
      root.style.setProperty('--succession-bg-tertiary', '#374151');
      root.style.setProperty('--succession-text-primary', '#F9FAFB');
      root.style.setProperty('--succession-text-secondary', '#D1D5DB');
      root.style.setProperty('--succession-text-tertiary', '#9CA3AF');
      root.style.setProperty('--succession-text-muted', '#6B7280');
      root.style.setProperty('--succession-border', '#374151');
      root.style.setProperty('--succession-border-hover', '#4B5563');
      root.style.setProperty('--succession-hover-bg', '#374151');
      root.style.setProperty('--succession-active-bg', '#4B5563');
      root.style.setProperty('--succession-focus-ring', 'rgba(74, 144, 226, 0.3)');
      
      // Demo-specific message colors for dark theme
      root.style.setProperty('--succession-message-sent-bg', '#4A90E2');
      root.style.setProperty('--succession-message-sent-text', '#ffffff');
      root.style.setProperty('--succession-message-received-bg', '#374151');
      root.style.setProperty('--succession-message-received-text', '#F9FAFB');
      root.style.setProperty('--succession-primary-demo', '#4A90E2');
      root.style.setProperty('--succession-primary-hover-demo', '#3A7BC8');
    }

    // Set additional succession-specific color variables (theme-independent)
    root.style.setProperty('--succession-success', '#10b981'); // green-500
    root.style.setProperty('--succession-success-bg', 'rgba(16, 185, 129, 0.1)');
    root.style.setProperty('--succession-warning', '#f59e0b'); // amber-500
    root.style.setProperty('--succession-warning-bg', 'rgba(245, 158, 11, 0.1)');
    root.style.setProperty('--succession-error', '#ef4444'); // red-500
    root.style.setProperty('--succession-error-bg', 'rgba(239, 68, 68, 0.1)');

    // Add marketplace theme class if not already present
    if (!root.classList.contains('marketplace-theme')) {
      root.classList.add('marketplace-theme');
    }

    // Ensure marketplace colors are preserved (re-apply if needed)
    const primaryColor = root.style.getPropertyValue('--succession-primary');
    if (!primaryColor && primaryRgb) {
      root.style.setProperty('--succession-primary', companyColors.primary_color);
      root.style.setProperty('--succession-primary-light', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`);
      root.style.setProperty('--succession-primary-hover', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.9)`);
    }
  }, [companyColors]);

  // Detect system preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      setSystemPreference(e.matches ? 'dark' : 'light');
    };

    setSystemPreference(mediaQuery.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Initialize theme on component mount
  useEffect(() => {
    let selectedTheme = 'light';
    
    // Check for saved theme preference
    const savedTheme = localStorage.getItem('succession-theme');
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      selectedTheme = savedTheme;
    }
    
    setTheme(selectedTheme);
    applyTheme(selectedTheme);
    setIsInitialized(true);
  }, [applyTheme]);

  // Listen for theme changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'succession-theme' && e.newValue && (e.newValue === 'light' || e.newValue === 'dark')) {
        setTheme(e.newValue);
      }
    };

    const handleCustomThemeChange = (e) => {
      if (e.detail && e.detail.theme && (e.detail.theme === 'light' || e.detail.theme === 'dark')) {
        setTheme(e.detail.theme);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('succession-theme-change', handleCustomThemeChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('succession-theme-change', handleCustomThemeChange);
    };
  }, []);

  // Toggle theme function
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('succession-theme', newTheme);
    
    // Add transition class for smooth switching
    const root = document.documentElement;
    root.classList.add('succession-theme-transition');
    
    applyTheme(newTheme);

    // Dispatch custom event for same-tab synchronization
    window.dispatchEvent(new CustomEvent('succession-theme-change', {
      detail: { theme: newTheme }
    }));

    // Remove transition class after animation
    setTimeout(() => {
      root.classList.remove('succession-theme-transition');
    }, 300);
  }, [theme, applyTheme]);

  // Apply theme when theme state changes
  useEffect(() => {
    if (isInitialized) {
      applyTheme(theme);
    }
  }, [theme, isInitialized, applyTheme]);

  // Reset to system preference
  const resetToSystemPreference = useCallback(() => {
    localStorage.removeItem('succession-theme');
    setTheme(systemPreference);
    applyTheme(systemPreference);
  }, [systemPreference, applyTheme]);

  // Context value (memoized to prevent unnecessary re-renders)
  const contextValue = useMemo(() => ({
    // Theme state
    theme,
    systemPreference,
    isInitialized,
    
    // Theme actions
    setTheme: (newTheme) => {
      if (newTheme === 'light' || newTheme === 'dark') {
        setTheme(newTheme);
        localStorage.setItem('succession-theme', newTheme);
        applyTheme(newTheme);
      }
    },
    toggleTheme,
    resetToSystemPreference,
    
    // Company settings (use memoized colors)
    company: companyColors,
    
    // User settings
    user,
    
    // Utility functions
    isLightTheme: theme === 'light',
    isDarkTheme: theme === 'dark',
    isSystemPreference: !localStorage.getItem('succession-theme'),
    
    // Color utilities
    hexToRgb,
    isLightColor,
    
    // CSS variable helpers
    getCSSVariable: (variableName) => {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(variableName)
        .trim();
    },
    
    setCSSVariable: (variableName, value) => {
      document.documentElement.style.setProperty(variableName, value);
    },

    // Debug helper to check current CSS variables
    debugCSSVariables: () => {
      const root = document.documentElement;
      return {
        theme: theme,
        companyColors: companyColors,
        cssVariables: {
          '--succession-primary': root.style.getPropertyValue('--succession-primary') || getComputedStyle(root).getPropertyValue('--succession-primary'),
          '--succession-secondary': root.style.getPropertyValue('--succession-secondary') || getComputedStyle(root).getPropertyValue('--succession-secondary'),
          '--succession-accent': root.style.getPropertyValue('--succession-accent') || getComputedStyle(root).getPropertyValue('--succession-accent'),
          '--succession-neutral': root.style.getPropertyValue('--succession-neutral') || getComputedStyle(root).getPropertyValue('--succession-neutral'),
          '--succession-bg-primary': root.style.getPropertyValue('--succession-bg-primary') || getComputedStyle(root).getPropertyValue('--succession-bg-primary'),
          '--succession-text-primary': root.style.getPropertyValue('--succession-text-primary') || getComputedStyle(root).getPropertyValue('--succession-text-primary'),
          '--succession-border': root.style.getPropertyValue('--succession-border') || getComputedStyle(root).getPropertyValue('--succession-border')
        }
      };
    }
  }), [theme, systemPreference, isInitialized, companyColors, user, toggleTheme, resetToSystemPreference, applyTheme]);

  // Don't render children until theme is initialized to prevent flash
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <SuccessionThemeContext.Provider value={contextValue}>
      {children}
    </SuccessionThemeContext.Provider>
  );
};

SuccessionThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  company: PropTypes.shape({
    primary_color: PropTypes.string,
    secondary_color: PropTypes.string,
    neutral_color: PropTypes.string,
    accent_color: PropTypes.string,
    background_color: PropTypes.string,
    avatar_background_color: PropTypes.string,
    avatar_directory: PropTypes.string,
    theme_name: PropTypes.string
  }),
  user: PropTypes.object,
  initialTheme: PropTypes.oneOf(['light', 'dark', null])
};

export default SuccessionThemeProvider; 